import { StepDot } from '@/components/ui/workspace/steps';
import { StageProgressBar } from '@/components/ui/stage-progress';
import { cn } from '@/lib/utils/cn';
import type { OverviewStage } from './overview-plan';
import { OVERVIEW_LINK, OverviewGo } from './overview-go';

/**
 * "Where the engagement stands" (5 Nov 2026): the stages left to right,
 * each a numbered dot (green tick when done, amber while open, a lock
 * while it waits on an earlier stage), one figure, one bar and one line
 * that links to where the stage's work is done. Discovery's stages sit
 * under a Discovery label and Selection's under a Selection label. The
 * stages come from `overviewStages`; this only draws them.
 */

// Tailwind needs the class names spelled out.
const COLS: Record<number, string> = {
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
  7: 'lg:grid-cols-7',
};
const SPAN: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
};

export function StageTrack({
  engagementId,
  stages,
}: {
  engagementId: string;
  stages: readonly OverviewStage[];
}) {
  const discovery = stages.filter((s) => s.group === 'discovery').length;
  const selection = stages.length - discovery;
  const cols = COLS[stages.length] ?? 'lg:grid-cols-6';

  return (
    <section
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
      aria-labelledby="stage-track-title"
      data-testid="stage-track"
    >
      <h2 id="stage-track-title" className="t-h3 mb-4">
        Where the engagement stands
      </h2>

      {discovery > 0 ? (
        <div aria-hidden="true" className={cn('mb-2.5 hidden gap-5 lg:grid', cols)}>
          <span className={cn(GROUP, SPAN[discovery])}>Discovery</span>
          <span className={cn(GROUP, SPAN[selection])}>Selection</span>
        </div>
      ) : null}

      <ol className={cn('m-0 grid list-none grid-cols-2 gap-x-5 gap-y-6 p-0 md:grid-cols-3', cols)}>
        {stages.map((s, i) => (
          <Stage
            key={s.key}
            engagementId={engagementId}
            stage={s}
            n={i + 1}
            last={i === stages.length - 1}
          />
        ))}
      </ol>
    </section>
  );
}

const GROUP =
  'border-b-2 border-ink-100 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500';

function Stage({
  engagementId,
  stage: s,
  n,
  last,
}: {
  engagementId: string;
  stage: OverviewStage;
  n: number;
  last: boolean;
}) {
  const locked = s.state === 'locked';
  const value = locked ? '–' : (s.value ?? '…');
  return (
    <li className="min-w-0" data-stage={s.key} data-state={s.state}>
      <div className="mb-2.5 flex items-center gap-2">
        <StepDot n={n} state={s.state} size="sm" />
        {!last ? (
          <span
            aria-hidden="true"
            className={cn(
              'hidden h-0.5 flex-1 lg:block',
              s.state === 'done' ? 'bg-posture-healthy' : 'bg-ink-100',
            )}
          />
        ) : null}
      </div>
      <div className={cn('text-body-sm font-semibold', locked ? 'text-ink-300' : 'text-ink-900')}>
        {s.name}
      </div>
      <div
        className={cn(
          'text-[22px] font-semibold leading-8 tabular-nums',
          locked ? 'text-ink-300' : s.state === 'todo' ? 'text-ink-500' : 'text-ink-900',
        )}
      >
        {value}
        {s.of && !locked ? (
          <span className="ml-1.5 text-body-sm font-medium text-ink-500">{s.of}</span>
        ) : null}
      </div>
      <StageProgressBar
        percent={locked ? 0 : s.percent}
        label={s.note || s.name}
        ariaLabel={`${s.name}: ${s.note || value}`}
        tone={s.critical ? 'critical' : s.state === 'done' ? 'done' : 'working'}
        size="md"
        hideLabel
        className="mb-2 mt-1.5"
      />
      {s.note ? (
        s.go ? (
          <OverviewGo
            engagementId={engagementId}
            go={s.go}
            className={cn(
              OVERVIEW_LINK,
              s.critical && 'text-posture-critical decoration-posture-critical/50',
            )}
          >
            {s.note}
          </OverviewGo>
        ) : (
          <span className="text-meta font-medium text-ink-500">{s.note}</span>
        )
      ) : null}
    </li>
  );
}
